import { dehydrate, QueryClient } from "@tanstack/react-query";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useSearchParams } from "next/navigation";
import { renderToString } from "react-dom/server";
import { getServerState, InstantSearchServerState } from "react-instantsearch";
import { v4 as uuidv4 } from "uuid";

import LayoutPage from "~/components/layout/layout-page";
import SearchProductGrid from "~/components/products/search-product-grid";
import SearchSkeleton from "~/components/search/search-skeleton";
import { useConfiguration } from "~/contexts/configuration";
import { usePageTracking } from "~/contexts/page-trackings";
import SearchProvider from "~/contexts/search";
import { mapLocale } from "~/services/algolia-utils";
import Eva from "~/services/eva";
import evaUtils from "~/utils/eva-utils";
import ssrUtils from "~/utils/ssr-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

type SearchPageProps = Omit<Props, "serverState"> & {
  serverState?: InstantSearchServerState;
};

export default function SearchPage(props: SearchPageProps) {
  const { page, serverState, algoliaUserToken, serverUrl, ...rest } = props;
  const { algoliaIndexId } = useConfiguration();
  const searchParams = useSearchParams();
  const searchKey = `${algoliaIndexId}[query]`;
  usePageTracking(
    "search",
    props.query ? undefined : { freezeOn: `${algoliaIndexId}[query]=${searchParams.get(searchKey)}` }
  );

  return (
    <LayoutPage {...rest} navColor={"gradient"}>
      {/* assuming that with empty serverUrl you are navigating the EVA version */}
      {serverUrl != "" ? (
        <SearchSkeleton
          serverUrl={serverUrl}
          serverState={serverState}
          algoliaIndexId={algoliaIndexId}
          noResultsComponents={page.fields.noResults}
          algoliaUserToken={algoliaUserToken}
        />
      ) : (
        <SearchProvider query={props.query}>
          <SearchProductGrid noResultsComponents={page.fields.noResults} />
        </SearchProvider>
      )}
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const pageData = await ssrUtils.getServerSideSearchPageData(context);
  const organizationUnitId = pageData.organizationUnitId;

  // fall back to EVA search
  if (process.env.NEXT_PUBLIC_DISABLE_ALGOLIA === "true") {
    let searchQuery = context.query["query"] as string;

    if (typeof searchQuery !== "string" || searchQuery.length === 0) {
      return {
        redirect: {
          destination: "/",
          permanent: false,
        },
      };
    }

    const eva = new Eva({ organizationUnitId, locale: context.locale });

    const queryClient = new QueryClient();
    await queryClient.prefetchInfiniteQuery({
      queryKey: ["search", searchQuery, Eva.DEFAULT_CATEGORY_SORT],
      queryFn: async () => await evaUtils.getProductsByQueryWithChildren(eva, searchQuery),
    });

    // TanStack query v4 cannot dehydrate infinite queries correctly.
    // See: https://github.com/TanStack/query/discussions/4854
    const dehydratedState = JSON.parse(JSON.stringify(dehydrate(queryClient)));

    return {
      props: {
        ...pageData,
        query: searchQuery,
        dehydratedState,
        serverUrl: "",
        algoliaUserToken: "",
      },
    };
  } else {
    const protocol = context.req.headers.referer?.split("://")[0] || "https";
    const serverUrl = `${protocol}://${context.req.headers.host}${context.req.url}`;
    const algoliaUserToken = context.req.cookies["algolia-user-token"] || `anonymous-${uuidv4()}`;
    const algoliaIndexId = mapLocale(
      pageData.configuration["App:Ecommerce:AlgoliaIndexPrefix"] ?? organizationUnitId,
      context.locale
    );

    const serverState = await getServerState(
      <SearchSkeleton serverUrl={serverUrl} algoliaIndexId={algoliaIndexId} algoliaUserToken={algoliaUserToken} />,
      {
        renderToString,
      }
    );

    return {
      props: {
        ...pageData,
        serverState,
        algoliaUserToken,
        serverUrl,
        query: "",
      },
    };
  }
}
